import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthContext";

export interface UserProfile {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  email: string | null;
  /** Operational balance, surfaced only in the account menu / billing, never the hero. */
  acu_balance: number | null;
  plan: string | null;
}

export function useProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setProfile(null); setLoading(false); return; }

    const fetch = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .maybeSingle();

      if (data) {
        setProfile(data as UserProfile);
      } else {
        // Fallback from auth metadata
        setProfile({
          id: user.id,
          display_name: user.user_metadata?.full_name ?? user.user_metadata?.name ?? user.email?.split("@")[0] ?? null,
          avatar_url: user.user_metadata?.avatar_url ?? null,
          email: user.email ?? null,
          acu_balance: null,
          plan: null,
        });
      }
      setLoading(false);
    };
    fetch();
  }, [user]);

  const getInitials = (): string => {
    const name = profile?.display_name || profile?.email || "";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase() || "??";
  };

  return { profile, loading, getInitials };
}
